import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Sparkle, FilePdf, Translate } from '@phosphor-icons/react';
import { openPdfInBrowser } from '../utils/pdfUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Phase 209/209i — Full English mirror of the Greek minutes.
 *
 * Layout:
 *   ── Meeting metadata (date / time / type / location)
 *   ── Apartments + Attendance register (read-only — names & numbers untouched)
 *   ── Management Committee (names read-only, ROLES editable in EN)
 *   ── Topics & Decisions (editable EN)
 *   ── Legal / Committee text / Quorum / Epilogue (editable EN)
 *   ── Single primary action: "Open PDF / Print"
 *
 * The dialog never blocks on missing translation: even before the user clicks
 * "Translate to English" the Greek source is shown so they can verify nothing
 * is missing before exporting.
 */

const EMPTY_TR = {
  topics: [],
  committee: [],
  attendance: [],
  legal_text: '',
  committee_text: '',
  quorum_text: '',
  closing_notes: '',
  location: '',
};

const EMPTY_SRC = {
  topics: [],
  committee: [],
  attendance: [],
  legal_text: '',
  committee_text: '',
  quorum_text: '',
  closing_notes: '',
  location: '',
  date: '',
  time: '',
  end_time: '',
  meeting_type: '',
};

export default function MeetingTranslateDialog({ open, onOpenChange, buildingId, meetingId, meetingDate, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [source, setSource] = useState(EMPTY_SRC);
  const [translation, setTranslation] = useState(EMPTY_TR);
  const [edited, setEdited] = useState(false);
  const [meta, setMeta] = useState({ exists: false, autoTranslatedAt: null, edited: false });

  const load = useCallback(async () => {
    if (!buildingId || !meetingId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/buildings/${buildingId}/meetings/${meetingId}/translations/en`);
      const s = r.data.source || {};
      setSource({
        topics: (s.topics || []).map((x) => ({ title: x.title || '', decision: x.decision || '' })),
        committee: (s.committee || []).map((m) => ({
          name: m.name || '',
          role: m.role || '',
          email: m.email || '',
          active: m.active !== false,
        })),
        attendance: s.attendance || [],
        legal_text: s.legal_text || '',
        committee_text: s.committee_text || '',
        quorum_text: s.quorum_text || '',
        closing_notes: s.closing_notes || '',
        location: s.location || '',
        date: s.date || '',
        time: s.time || '',
        end_time: s.end_time || '',
        meeting_type: s.meeting_type || '',
      });

      if (r.data.exists) {
        const t = r.data.translations || {};
        // Phase 209k — Pre-fill committee names with EN translation, falling
        // back to the Greek source name so the row is never blank.
        const srcCommittee = s.committee || [];
        const srcAttendance = s.attendance || [];
        setTranslation({
          topics: (t.topics || []).map((x) => ({ title: x.title || '', decision: x.decision || '' })),
          committee: srcCommittee.map((srcM, i) => {
            const trM = (t.committee || [])[i] || {};
            return {
              role: trM.role || '',
              name: trM.name || srcM.name || srcM.custom_name || '',
            };
          }),
          attendance: srcAttendance.map((srcA, i) => {
            const trA = (t.attendance || [])[i] || {};
            return {
              owner_name: trA.owner_name || srcA.owner_name || '',
              represented_by: trA.represented_by || srcA.represented_by || '',
            };
          }),
          legal_text: t.legal_text || '',
          committee_text: t.committee_text || '',
          quorum_text: t.quorum_text || '',
          closing_notes: t.closing_notes || '',
          location: t.location || '',
        });
        setMeta({ exists: true, autoTranslatedAt: t.auto_translated_at, edited: !!t.edited });
      } else {
        // Phase 209k — Empty editable fields, but pre-populate names with
        // the Greek source so the user always sees something to refine.
        const srcCommittee = s.committee || [];
        const srcAttendance = s.attendance || [];
        setTranslation({
          topics: (s.topics || []).map(() => ({ title: '', decision: '' })),
          committee: srcCommittee.map((m) => ({
            role: '',
            name: m.name || m.custom_name || '',
          })),
          attendance: srcAttendance.map((a) => ({
            owner_name: a.owner_name || '',
            represented_by: a.represented_by || '',
          })),
          legal_text: '',
          committee_text: '',
          quorum_text: '',
          // Phase 209j — Seed the EN epilogue with the active EN template.
          closing_notes: (r.data.translations && r.data.translations.closing_notes) || '',
          location: '',
        });
        setMeta({ exists: false, autoTranslatedAt: null, edited: false });
      }
      setEdited(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Σφάλμα φόρτωσης');
    } finally {
      setLoading(false);
    }
  }, [buildingId, meetingId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const autoTranslate = async () => {
    setTranslating(true);
    try {
      const r = await axios.post(`${API}/buildings/${buildingId}/meetings/${meetingId}/translate-to-en`);
      const t = r.data.translations || {};
      // Phase 209k — Always pad/align with the Greek source so editable rows
      // are never lost if the LLM truncates an array.
      const srcCommittee = source.committee || [];
      const srcAttendance = source.attendance || [];
      setTranslation({
        topics: (t.topics || []).map((x) => ({ title: x.title || '', decision: x.decision || '' })),
        committee: srcCommittee.map((srcM, i) => {
          const trM = (t.committee || [])[i] || {};
          return {
            role: trM.role || '',
            name: trM.name || srcM.name || srcM.custom_name || '',
          };
        }),
        attendance: srcAttendance.map((srcA, i) => {
          const trA = (t.attendance || [])[i] || {};
          return {
            owner_name: trA.owner_name || srcA.owner_name || '',
            represented_by: trA.represented_by || srcA.represented_by || '',
          };
        }),
        legal_text: t.legal_text || '',
        committee_text: t.committee_text || '',
        quorum_text: t.quorum_text || '',
        closing_notes: t.closing_notes || '',
        location: t.location || '',
      });
      setMeta({ exists: true, autoTranslatedAt: t.auto_translated_at, edited: false });
      setEdited(false);
      toast.success('Auto-translation completed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  const saveTranslationSilent = async () => {
    await axios.put(`${API}/buildings/${buildingId}/meetings/${meetingId}/translations/en`, {
      topics: translation.topics,
      committee: translation.committee,
      attendance: translation.attendance,
      legal_text: translation.legal_text,
      committee_text: translation.committee_text,
      quorum_text: translation.quorum_text,
      closing_notes: translation.closing_notes,
      location: translation.location,
    });
    setEdited(false);
    setMeta((m) => ({ ...m, exists: true, edited: true }));
    onSaved && onSaved();
  };

  const openAndPrintEnglishPdf = async () => {
    if (!meta.exists) {
      toast.error('Πατήστε πρώτα "Translate to English"');
      return;
    }
    setExporting(true);
    try {
      // Persist any unsaved edits so the PDF reflects the latest text.
      if (edited) await saveTranslationSilent();
      // Phase 735.30 — Route via the in-app PDF viewer helper.
      await openPdfInBrowser(
        `/buildings/${buildingId}/meetings/${meetingId}/pdf`,
        { lang: 'en', disposition: 'inline' },
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to generate document');
    } finally {
      setExporting(false);
    }
  };

  const updateTopic = (idx, field, value) => {
    setEdited(true);
    setTranslation((prev) => ({
      ...prev,
      topics: prev.topics.map((t, i) => (i === idx ? { ...t, [field]: value } : t)),
    }));
  };
  const updateCommittee = (idx, field, value) => {
    setEdited(true);
    setTranslation((prev) => ({
      ...prev,
      committee: prev.committee.map((m, i) => (i === idx ? { ...m, [field]: value } : m)),
    }));
  };
  const updateAttendance = (idx, field, value) => {
    setEdited(true);
    setTranslation((prev) => ({
      ...prev,
      attendance: prev.attendance.map((a, i) => (i === idx ? { ...a, [field]: value } : a)),
    }));
  };
  const updateField = (field, value) => {
    setEdited(true);
    setTranslation((prev) => ({ ...prev, [field]: value }));
  };

  const presentCount = (source.attendance || []).filter((a) => a.present).length;
  const absentCount = (source.attendance || []).filter((a) => !a.present).length;
  const pctOf = (a) => (a.share_percentage ?? a.percentage ?? 0);
  const presentPct = (source.attendance || []).reduce((s, a) => s + (a.present ? pctOf(a) : 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-7xl max-h-[92vh] overflow-hidden p-0 rounded-none flex flex-col"
        data-testid="translate-dialog"
      >
        <DialogHeader className="px-6 py-4 border-b border-zinc-200">
          <DialogTitle className="flex items-center gap-2 text-base font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
            <Translate size={20} className="text-blue-600" />
            English Version – Full Mirror & Edit
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Όλα τα δεδομένα της Ελληνικής έκδοσης. Επεξεργασία των μεταφρασμένων κειμένων πριν το export.
          </p>
        </DialogHeader>

        {/* Toolbar */}
        <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            onClick={autoTranslate}
            disabled={translating}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-none text-xs"
            data-testid="auto-translate-btn"
          >
            <Sparkle size={14} className="mr-1.5" />
            {translating ? 'Translating…' : meta.exists ? 'Re-translate (overwrite)' : 'Translate to English'}
          </Button>
          {meta.exists && (
            <span className="text-[10px] text-zinc-500 ml-2">
              {meta.edited ? '✏️ Edited' : '🤖 Auto-translated'}
              {meta.autoTranslatedAt && (
                <> · {new Date(meta.autoTranslatedAt).toLocaleString()}</>
              )}
            </span>
          )}
          <div className="flex-1" />
          <Button
            type="button"
            onClick={openAndPrintEnglishPdf}
            disabled={!meta.exists || exporting || translating}
            className="bg-emerald-600 hover:bg-emerald-700 rounded-none text-xs"
            data-testid="open-print-en-btn"
          >
            <FilePdf size={14} className="mr-1.5" />
            {exporting ? 'Preparing document…' : 'Open PDF / Print'}
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {loading ? (
            <p className="text-sm text-zinc-400 text-center py-12">Φόρτωση…</p>
          ) : (
            <>
              {!meta.exists && (
                <div className="bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-900">
                  ℹ️ Δεν υπάρχει αγγλική έκδοση ακόμα. Πατήστε <strong>"Translate to English"</strong> για να γίνει αυτόματη μετάφραση. Όλα τα read-only πεδία (διαμερίσματα, ονόματα, ποσοστά) θα παραμείνουν ίδια.
                </div>
              )}

              {/* 1. Meeting metadata */}
              <Section title="MEETING METADATA / Στοιχεία Συνέλευσης">
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200 border border-zinc-200">
                  <div className="px-3 py-2.5 bg-zinc-50/50 text-xs">
                    <DataRow label="Ημερομηνία" value={source.date} />
                    <DataRow label="Ώρα" value={`${source.time || '—'} – ${source.end_time || '—'}`} />
                    <DataRow label="Τύπος" value={source.meeting_type} />
                    <DataRow label="Τοποθεσία (Ελληνικά)" value={source.location} />
                  </div>
                  <div className="px-3 py-2.5">
                    <DataRow label="Date" value={source.date} readonly />
                    <DataRow label="Time" value={`${source.time || '—'} – ${source.end_time || '—'}`} readonly />
                    <DataRow label="Type" value={source.meeting_type} readonly />
                    <Label className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mt-2 block">Location (English) – Editable</Label>
                    <Input
                      value={translation.location}
                      onChange={(e) => updateField('location', e.target.value)}
                      className="mt-1 text-xs h-8 rounded-none focus:border-blue-500"
                      data-testid="en-location"
                    />
                  </div>
                </div>
              </Section>

              {/* 2. Apartments / Attendance */}
              <Section title={`ATTENDANCE / Παρουσιολόγιο  ·  Present: ${presentCount} (${presentPct.toFixed(2)}%)  ·  Absent: ${absentCount}`}>
                {(source.attendance || []).length === 0 ? (
                  <p className="text-xs text-zinc-400 italic">Δεν υπάρχει αρχείο παρουσιών.</p>
                ) : (
                  <div className="border border-zinc-200 overflow-hidden">
                    <table className="w-full text-xs" data-testid="attendance-table">
                      <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                        <tr>
                          <th className="px-3 py-2 text-left">Apartment / Διαμέρισμα</th>
                          <th className="px-3 py-2 text-left">Owner (Greek) / Ιδιοκτήτης</th>
                          <th className="px-3 py-2 text-left">Owner (English) – Editable</th>
                          <th className="px-3 py-2 text-left">Represented by (English) – Editable</th>
                          <th className="px-3 py-2 text-right">Share %</th>
                          <th className="px-3 py-2 text-center">Present / Παρών</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {(source.attendance || []).map((a, i) => (
                          <tr key={i} data-testid={`attendance-row-${i}`}>
                            <td className="px-3 py-1.5 font-mono text-zinc-700">{a.apartment_number || '—'}</td>
                            <td className="px-3 py-1.5 text-zinc-700">
                              {a.owner_name || '—'}
                              {a.represented_by ? (
                                <div className="text-[10px] text-zinc-500 mt-0.5">
                                  Εκπροσωπείται από: {a.represented_by}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-3 py-1.5">
                              <Input
                                value={(translation.attendance[i] || {}).owner_name || ''}
                                onChange={(e) => updateAttendance(i, 'owner_name', e.target.value)}
                                placeholder={a.owner_name || ''}
                                className="h-7 text-xs rounded-none focus:border-blue-500"
                                data-testid={`en-attendance-name-${i}`}
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <Input
                                value={(translation.attendance[i] || {}).represented_by || ''}
                                onChange={(e) => updateAttendance(i, 'represented_by', e.target.value)}
                                placeholder={a.represented_by || '— (no proxy)'}
                                className="h-7 text-xs rounded-none focus:border-blue-500"
                                data-testid={`en-attendance-rep-${i}`}
                                disabled={!a.represented_by && !(translation.attendance[i] || {}).represented_by}
                              />
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-600">{pctOf(a).toFixed(2)}%</td>
                            <td className="px-3 py-1.5 text-center">
                              {a.present ? (
                                <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">✓ Present</span>
                              ) : (
                                <span className="inline-block px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 text-[10px]">✗ Absent</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[10px] text-zinc-400 mt-2 italic">
                  Apartment numbers, share % and Present/Absent status are NOT translated. Owner names AND proxy names ("Represented by") are editable per-meeting (transliteration recommended — the EN PDF prefers the English value when filled, otherwise falls back to the Greek source).
                </p>
              </Section>

              {/* 3. Committee */}
              <Section title="MANAGEMENT COMMITTEE / Επιτροπή Διαχείρισης">
                {(source.committee || []).length === 0 ? (
                  <p className="text-xs text-zinc-400 italic">Δεν υπάρχουν μέλη επιτροπής.</p>
                ) : (
                  <div className="border border-zinc-200 overflow-hidden">
                    <table className="w-full text-xs" data-testid="committee-table">
                      <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                        <tr>
                          <th className="px-3 py-2 text-left">Name (Greek)</th>
                          <th className="px-3 py-2 text-left">Name (English) – Editable</th>
                          <th className="px-3 py-2 text-left">Greek Role</th>
                          <th className="px-3 py-2 text-left">English Role – Editable</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-center">Active</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {source.committee.map((m, i) => (
                          <tr key={i} data-testid={`committee-row-${i}`}>
                            <td className="px-3 py-1.5 text-zinc-700 font-medium">{m.name || m.custom_name || '—'}</td>
                            <td className="px-3 py-1.5">
                              <Input
                                value={(translation.committee[i] || {}).name || ''}
                                onChange={(e) => updateCommittee(i, 'name', e.target.value)}
                                placeholder={m.name || m.custom_name || ''}
                                className="h-7 text-xs rounded-none focus:border-blue-500"
                                data-testid={`en-committee-name-${i}`}
                              />
                            </td>
                            <td className="px-3 py-1.5 text-zinc-600">{m.role || '—'}</td>
                            <td className="px-3 py-1.5">
                              <Input
                                value={(translation.committee[i] || {}).role || ''}
                                onChange={(e) => updateCommittee(i, 'role', e.target.value)}
                                placeholder="e.g. Chairperson, Secretary, Treasurer"
                                className="h-7 text-xs rounded-none focus:border-blue-500"
                                data-testid={`en-committee-${i}`}
                              />
                            </td>
                            <td className="px-3 py-1.5 text-zinc-500 text-[11px]">{m.email || '—'}</td>
                            <td className="px-3 py-1.5 text-center">
                              {m.active ? '✓' : <span className="text-zinc-300">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[10px] text-zinc-400 mt-2 italic">
                  Names are editable per-meeting. The English PDF uses the English name when filled; otherwise it falls back to the Greek source.
                </p>
              </Section>

              {/* 4. Topics & Decisions */}
              <Section title="TOPICS & DECISIONS / Θέματα & Αποφάσεις">
                {(source.topics || []).length === 0 ? (
                  <p className="text-xs text-zinc-400 italic">Δεν υπάρχουν θέματα.</p>
                ) : (
                  source.topics.map((srcTopic, i) => {
                    const trTopic = translation.topics[i] || { title: '', decision: '' };
                    return (
                      <div key={i} className="border border-zinc-200 mb-3" data-testid={`topic-row-${i}`}>
                        <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                          Topic {i + 1}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
                          <FieldPair labelGr="Τίτλος (Ελληνικά)" labelEn="Title (English) – Editable" gr={srcTopic.title} en={trTopic.title} onEnChange={(v) => updateTopic(i, 'title', v)} multiline={false} testIdSuffix={`topic-${i}-title`} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200 border-t border-zinc-200">
                          <FieldPair labelGr="Απόφαση (Ελληνικά)" labelEn="Decision (English) – Editable" gr={srcTopic.decision} en={trTopic.decision} onEnChange={(v) => updateTopic(i, 'decision', v)} multiline testIdSuffix={`topic-${i}-decision`} />
                        </div>
                      </div>
                    );
                  })
                )}
              </Section>

              {/* 5. Quorum text */}
              <Section title="QUORUM TEXT / Κείμενο Απαρτίας">
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200 border border-zinc-200">
                  <FieldPair labelGr="Κείμενο Απαρτίας (Ελληνικά)" labelEn="Quorum Text (English) – Editable" gr={source.quorum_text} en={translation.quorum_text} onEnChange={(v) => updateField('quorum_text', v)} multiline testIdSuffix="quorum-text" />
                </div>
              </Section>

              {/* 6. Legal text */}
              <Section title="LEGAL TEXT / Νομικό Κείμενο">
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200 border border-zinc-200">
                  <FieldPair labelGr="Νομικό Κείμενο (Ελληνικά)" labelEn="Legal Text (English) – Editable" gr={source.legal_text} en={translation.legal_text} onEnChange={(v) => updateField('legal_text', v)} multiline testIdSuffix="legal-text" />
                </div>
              </Section>

              {/* 7. Committee text */}
              <Section title="COMMITTEE TEXT / Κείμενο Επιτροπής">
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200 border border-zinc-200">
                  <FieldPair labelGr="Κείμενο Επιτροπής (Ελληνικά)" labelEn="Committee Text (English) – Editable" gr={source.committee_text} en={translation.committee_text} onEnChange={(v) => updateField('committee_text', v)} multiline testIdSuffix="committee-text" />
                </div>
              </Section>

              {/* 8. Epilogue */}
              <Section title="EPILOGUE / Επίλογος">
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200 border border-zinc-200">
                  <FieldPair labelGr="Επίλογος (Ελληνικά)" labelEn="Epilogue (English) – Editable" gr={source.closing_notes} en={translation.closing_notes} onEnChange={(v) => updateField('closing_notes', v)} multiline testIdSuffix="closing-notes" />
                </div>
              </Section>
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-zinc-200 bg-zinc-50">
          <Button onClick={() => onOpenChange(false)} variant="outline" className="rounded-none" data-testid="close-translate-dialog">
            Κλείσιμο
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-700 mb-2 pb-1 border-b border-zinc-200" style={{ fontFamily: 'var(--font-heading)' }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function DataRow({ label, value, readonly }) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <span className={`text-[10px] uppercase tracking-wider font-semibold w-32 ${readonly ? 'text-blue-700' : 'text-zinc-500'}`}>{label}</span>
      <span className="text-zinc-800">{value || '—'}</span>
    </div>
  );
}

function FieldPair({ labelGr, labelEn, gr, en, onEnChange, multiline, testIdSuffix }) {
  return (
    <>
      <div className="px-3 py-2.5 bg-zinc-50/50">
        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{labelGr}</Label>
        <div className="mt-1 text-xs text-zinc-700 whitespace-pre-wrap min-h-[20px]" data-testid={`gr-${testIdSuffix}`}>
          {gr || <span className="italic text-zinc-300">—</span>}
        </div>
      </div>
      <div className="px-3 py-2.5">
        <Label className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">{labelEn}</Label>
        {multiline ? (
          <textarea
            value={en}
            onChange={(e) => onEnChange(e.target.value)}
            rows={Math.max(2, Math.min(8, (en || '').split('\n').length + 1))}
            className="mt-1 w-full text-xs border border-zinc-300 px-2 py-1.5 rounded-none focus:border-blue-500 focus:outline-none"
            data-testid={`en-${testIdSuffix}`}
          />
        ) : (
          <Input
            value={en}
            onChange={(e) => onEnChange(e.target.value)}
            className="mt-1 text-xs h-8 rounded-none focus:border-blue-500"
            data-testid={`en-${testIdSuffix}`}
          />
        )}
      </div>
    </>
  );
}
